import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import Globe, { type GlobeInstance } from "globe.gl";
import {
  Mesh,
  MeshBasicMaterial,
  MeshPhongMaterial,
  ShaderMaterial,
  SphereGeometry,
  TextureLoader,
  Vector2,
  type IUniform,
  type Mesh as MeshType,
  type Object3D,
  type Texture,
} from "three";
import { z } from "zod";
import type { GlobeArc, GlobePoint, GlobePlaylist } from "#shared/schemas/globe.ts";
import {
  ATMOSPHERE_ALTITUDE,
  ATMOSPHERE_COLOR,
  DAY_NIGHT_SHADER,
  advanceAmbianceClock,
  seedAmbianceClock,
  type AmbianceClock,
} from "@web/features/globe/ambiance.ts";
import {
  BORDER_ALTITUDE,
  BORDER_STROKE,
  BORDER_STROKE_WIDTH,
  CLOUDS_ALT,
  CLOUDS_OPACITY,
  CLOUDS_ROTATION_SPEED,
  GLOBE_ASSETS,
  HIGHLIGHT_BORDER_ALTITUDE,
  HIGHLIGHT_BORDER_STROKE,
  HIGHLIGHT_BORDER_STROKE_WIDTH,
  LABEL_ALTITUDE,
  LABEL_COLOR_ARTIST,
  LABEL_MAX_ALTITUDE,
  LABEL_SIZE_ARTIST,
  MIN_CAMERA_ALTITUDE,
  PIN_HALO_COLOR,
  SHOT_LABEL_ALTITUDE,
  SHOT_LABEL_COLOR,
  SHOT_LABEL_SIZE,
} from "@web/features/globe/assets.ts";
import { geojsonToBorderPaths, type BorderPath } from "@web/features/globe/borderPaths.ts";
import { browseFlyTo } from "@web/features/globe/flyTo.ts";
import {
  previewShot as runPreviewShot,
  recordFlight,
  aspectPixelSize,
} from "@web/features/globe/recordFlight.ts";
import type { GlobeLayerState } from "@web/features/globe/layers.ts";
import type { ShotAspect, ShotPresetValues } from "@web/features/globe/shotPreset.ts";
import {
  MAX_ARCS_PER_PLAYLIST,
  PLAYLIST_HUES,
  PLAYLIST_OBJECT_ALTITUDE,
  POINT_KIND_STYLES,
  pointRadiusForCount,
} from "@web/features/globe/styles.ts";
import { cn } from "@web/lib/utils.ts";

const IDLE_RESUME_MS = 15_000;

type RenderPoint = GlobePoint & {
  color: string;
  radius: number;
  altitude: number;
  isHalo?: boolean;
};

type PlaylistObject = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  altitude: number;
  color: string;
};

type ArcDatum = GlobeArc & { playlistId: string };

const CountriesGeoJsonSchema = z.object({
  features: z.array(
    z.object({
      type: z.string(),
      properties: z
        .object({
          ISO_A2: z.string().optional(),
          ADMIN: z.string().optional(),
          NAME: z.string().optional(),
        })
        .passthrough(),
      geometry: z.unknown(),
    }),
  ),
});

type CountryLabel = {
  lat: number;
  lng: number;
  text: string;
  color: string;
  size: number;
  hasArtists: boolean;
};

export type GlobeCanvasHandle = {
  focusOnPoint: (point: GlobePoint) => Promise<void>;
  previewShot: (
    point: GlobePoint,
    preset: ShotPresetValues,
    aspect: ShotAspect,
  ) => Promise<void>;
  recordShot: (
    point: GlobePoint,
    preset: ShotPresetValues,
    aspect: ShotAspect,
    hooks: {
      enterCleanStage: () => void;
      exitCleanStage: () => void;
    },
    signal?: AbortSignal,
  ) => Promise<Blob>;
  getGlobe: () => GlobeInstance | null;
  isBusy: () => boolean;
};

function playlistCentroid(
  codes: string[],
  pointsByCode: Map<string, GlobePoint>,
): { lat: number; lng: number } | null {
  let lat = 0;
  let lng = 0;
  let n = 0;
  for (const code of codes) {
    const p = pointsByCode.get(code);
    if (!p) continue;
    lat += p.lat;
    lng += p.lng;
    n += 1;
  }
  if (n === 0) return null;
  return { lat: lat / n, lng: lng / n };
}

function glowSphere(color: string): Object3D {
  const geo = new SphereGeometry(1.8, 16, 16);
  const mat = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.85,
  });
  const mesh = new Mesh(geo, mat);
  const haloGeo = new SphereGeometry(2.6, 12, 12);
  const haloMat = new MeshBasicMaterial({
    color,
    transparent: true,
    opacity: 0.22,
  });
  mesh.add(new Mesh(haloGeo, haloMat));
  return mesh;
}

function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function setVec2Uniform(
  uniforms: Record<string, IUniform>,
  key: string,
  x: number,
  y: number,
): void {
  const u = uniforms[key];
  if (!u || !(u.value instanceof Vector2)) return;
  u.value.set(x, y);
}

export const GlobeCanvas = forwardRef<
  GlobeCanvasHandle,
  {
    points: GlobePoint[];
    playlists: GlobePlaylist[];
    selectedPlaylistIds: string[];
    layers: GlobeLayerState;
    focusedPointId: string | null;
    cleanStage: boolean;
    tooltipsOff: boolean;
    onCountryClick: (point: GlobePoint, playlistId: string | null) => void;
    onPlaylistObjectClick: (playlistId: string) => void;
    onUserInteract: () => void;
    onReady: () => void;
    onFocusComplete: (pointId: string) => void;
  }
>(function GlobeCanvas(
  {
    points,
    playlists,
    selectedPlaylistIds,
    layers,
    focusedPointId,
    cleanStage,
    tooltipsOff,
    onCountryClick,
    onPlaylistObjectClick,
    onUserInteract,
    onReady,
    onFocusComplete,
  },
  ref,
) {
  const hostRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<GlobeInstance | null>(null);
  const recordViewportRef = useRef<ShotAspect | null>(null);
  const [recordAspect, setRecordAspect] = useState<ShotAspect | null>(null);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clockRef = useRef<AmbianceClock>(seedAmbianceClock());
  const sunMaterialRef = useRef<ShaderMaterial | null>(null);
  const cloudsRef = useRef<MeshType | null>(null);
  const borderPathsRef = useRef<BorderPath[]>([]);
  /** Current day texture URL (low-res → hi-res after lazy load). */
  const dayTextureUrlRef = useRef<string>(GLOBE_ASSETS.earthDay);
  const dayTexRef = useRef<Texture | null>(null);
  const nightTexRef = useRef<Texture | null>(null);
  const dayPhongRef = useRef<MeshPhongMaterial | null>(null);
  const highlightCountryRef = useRef<string | null>(null);
  /** During preview/record — strip heavy layers so captureStream stays smooth. */
  const shotPerfRef = useRef(false);
  const shotLabelPointRef = useRef<GlobePoint | null>(null);
  const shotPointsOverrideRef = useRef<RenderPoint[] | null>(null);
  const arcsRef = useRef<ArcDatum[]>([]);
  const objectsRef = useRef<PlaylistObject[]>([]);
  const cameraAltRef = useRef(2.5);
  const renderPointsRef = useRef<RenderPoint[]>([]);
  const layersRef = useRef(layers);
  layersRef.current = layers;
  const pointsByCodeRef = useRef(new Map<string, GlobePoint>());
  const selectedRef = useRef(selectedPlaylistIds);
  selectedRef.current = selectedPlaylistIds;
  const playlistsByIdRef = useRef(new Map<string, GlobePlaylist>());
  const focusedRef = useRef(focusedPointId);
  focusedRef.current = focusedPointId;
  const tooltipsOffRef = useRef(tooltipsOff);
  tooltipsOffRef.current = tooltipsOff;
  const cleanStageRef = useRef(cleanStage);
  cleanStageRef.current = cleanStage;
  const focusLockRef = useRef(false);
  const flyingRef = useRef(false);
  const readyRef = useRef(false);
  const onCountryClickRef = useRef(onCountryClick);
  onCountryClickRef.current = onCountryClick;
  const onPlaylistObjectClickRef = useRef(onPlaylistObjectClick);
  onPlaylistObjectClickRef.current = onPlaylistObjectClick;
  const onUserInteractRef = useRef(onUserInteract);
  onUserInteractRef.current = onUserInteract;
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  const onFocusCompleteRef = useRef(onFocusComplete);
  onFocusCompleteRef.current = onFocusComplete;

  const pointsByCode = useMemo(() => {
    const m = new Map<string, GlobePoint>();
    for (const p of points) {
      if (p.countryCode) m.set(p.countryCode, p);
    }
    pointsByCodeRef.current = m;
    return m;
  }, [points]);

  const playlistsById = useMemo(() => {
    const m = new Map<string, GlobePlaylist>();
    for (const pl of playlists) m.set(pl.id, pl);
    playlistsByIdRef.current = m;
    return m;
  }, [playlists]);

  const maxCount = useMemo(
    () => Math.max(1, ...points.map((p) => p.artistCount)),
    [points],
  );

  const involvedCodes = useMemo(() => {
    const set = new Set<string>();
    for (const id of selectedPlaylistIds) {
      const pl = playlistsById.get(id);
      if (!pl) continue;
      for (const code of pl.artistCountryCodes.slice(0, MAX_ARCS_PER_PLAYLIST)) {
        set.add(code);
      }
    }
    return set;
  }, [selectedPlaylistIds, playlistsById]);

  const focusActive = selectedPlaylistIds.length > 0;

  const renderPoints: RenderPoint[] = useMemo(() => {
    if (!layers.artists) return [];
    let base = points;
    if (layers.focusedOnly && focusedPointId) {
      base = points.filter((p) => p.id === focusedPointId);
    }
    const out: RenderPoint[] = [];
    for (const p of base) {
      const style = POINT_KIND_STYLES[p.kind];
      const involved =
        !focusActive ||
        (p.countryCode != null && involvedCodes.has(p.countryCode)) ||
        p.kind !== "country";
      let color = involved ? style.color : style.dimColor;
      if (focusActive && involved && p.countryCode) {
        for (let i = 0; i < selectedPlaylistIds.length; i++) {
          const pl = playlistsById.get(selectedPlaylistIds[i]!);
          if (pl?.artistCountryCodes.includes(p.countryCode)) {
            color = PLAYLIST_HUES[i % PLAYLIST_HUES.length]!;
            break;
          }
        }
      }
      if (focusedPointId && p.id === focusedPointId) {
        color = style.color;
      }
      const radius = pointRadiusForCount(p.kind, p.artistCount, maxCount);
      const altitude = style.altitude;
      // Dark halo under pin for daylight readability (day-only + terminator day side).
      out.push({
        ...p,
        id: `halo:${p.id}`,
        color: PIN_HALO_COLOR,
        radius: radius * 1.7,
        altitude: altitude * 0.4,
        isHalo: true,
      });
      out.push({ ...p, color, radius, altitude });
    }
    return out;
  }, [
    points,
    layers.artists,
    layers.focusedOnly,
    focusedPointId,
    focusActive,
    involvedCodes,
    maxCount,
    selectedPlaylistIds,
    playlistsById,
  ]);

  const { objects, arcs } = useMemo(() => {
    if (!layers.playlists) return { objects: [] as PlaylistObject[], arcs: [] as ArcDatum[] };
    const objectsOut: PlaylistObject[] = [];
    const arcsOut: ArcDatum[] = [];
    selectedPlaylistIds.forEach((id, i) => {
      const pl = playlistsById.get(id);
      if (!pl) return;
      const color = PLAYLIST_HUES[i % PLAYLIST_HUES.length]!;
      const codes = pl.artistCountryCodes;
      const capped = codes.slice(0, MAX_ARCS_PER_PLAYLIST);
      const centre = playlistCentroid(capped.length > 0 ? capped : codes, pointsByCode);
      if (!centre) return;
      objectsOut.push({
        id: pl.id,
        name: pl.name,
        lat: centre.lat,
        lng: centre.lng,
        altitude: PLAYLIST_OBJECT_ALTITUDE,
        color,
      });
      for (const code of capped) {
        const target = pointsByCode.get(code);
        if (!target) continue;
        arcsOut.push({
          startLat: centre.lat,
          startLng: centre.lng,
          endLat: target.lat,
          endLng: target.lng,
          color,
          altitude: 0.2,
          playlistId: pl.id,
        });
      }
    });
    return { objects: objectsOut, arcs: arcsOut };
  }, [layers.playlists, selectedPlaylistIds, playlistsById, pointsByCode]);

  function applyBorders(globe: GlobeInstance, on: boolean) {
    const highlight = highlightCountryRef.current;
    if (!on && !highlight) {
      globe.pathsData([]);
      globe.labelsData([]);
      return;
    }
    // Shot highlight → ONLY that country's rings. Drawing the whole world
    // border set during a close fly-in is what stuttered into the WebM.
    if (highlight) {
      const paths = borderPathsRef.current.filter((p) => p.code === highlight);
      globe
        .pathsData(paths)
        .pathPoints("coords")
        .pathPointLng((p: number[]) => p[0]!)
        .pathPointLat((p: number[]) => p[1]!)
        .pathPointAlt(() => HIGHLIGHT_BORDER_ALTITUDE)
        .pathColor(() => HIGHLIGHT_BORDER_STROKE)
        .pathStroke(HIGHLIGHT_BORDER_STROKE_WIDTH)
        .pathDashLength(1)
        .pathDashGap(0)
        .pathDashAnimateTime(0)
        .pathTransitionDuration(0);
      // Keep the shot country name — don't wipe it when applying the outline.
      if (shotLabelPointRef.current) showShotCountryLabel(globe, shotLabelPointRef.current);
      else globe.labelsData([]);
      return;
    }
    // Static line paths — no polygon tessellation, no hover hit-tests.
    globe
      .pathsData(borderPathsRef.current)
      .pathPoints("coords")
      .pathPointLng((p: number[]) => p[0]!)
      .pathPointLat((p: number[]) => p[1]!)
      .pathPointAlt(() => BORDER_ALTITUDE)
      .pathColor(() => BORDER_STROKE)
      .pathStroke(BORDER_STROKE_WIDTH)
      .pathDashLength(1)
      .pathDashGap(0)
      .pathDashAnimateTime(0)
      .pathTransitionDuration(0);
    if (on) refreshLabels(globe);
    else globe.labelsData([]);
  }

  /** Shrink pins as the camera closes in — fixed radii fill the frame at shot altitudes. */
  function radiusScaleForAltitude(alt: number): number {
    if (alt >= 1.8) return 1;
    if (alt <= 0.35) return 0.12;
    const t = (alt - 0.35) / (1.8 - 0.35);
    return 0.12 + t * t * 0.88;
  }

  function pushPoints(globe: GlobeInstance) {
    const base = shotPointsOverrideRef.current ?? renderPointsRef.current;
    const scale = radiusScaleForAltitude(cameraAltRef.current);
    if (scale >= 0.999) {
      globe.pointsData(base);
      return;
    }
    globe.pointsData(
      base.map((p) => ({
        ...p,
        radius: p.radius * scale,
      })),
    );
  }

  function setHighlightCountry(globe: GlobeInstance, code: string | null) {
    highlightCountryRef.current = code;
    applyBorders(globe, layersRef.current.borders);
  }

  /** Big country name for the shot — stays on for the whole preview/record. */
  function showShotCountryLabel(globe: GlobeInstance, point: GlobePoint) {
    globe
      .labelsData([
        {
          lat: point.lat,
          lng: point.lng,
          text: point.label,
          color: SHOT_LABEL_COLOR,
          size: SHOT_LABEL_SIZE,
          hasArtists: true,
        },
      ])
      .labelAltitude(SHOT_LABEL_ALTITUDE);
  }

  /**
   * CaptureStream records live frames — browser jank = video jank.
   * Strip clouds / other labels / arcs / other pins for the shot window.
   */
  function enterShotPerf(globe: GlobeInstance, point: GlobePoint) {
    shotPerfRef.current = true;
    shotLabelPointRef.current = point;
    applyClouds(false);
    globe.arcsData([]);
    globe.objectsData([]);
    const pinId = point.id;
    const haloId = `halo:${point.id}`;
    shotPointsOverrideRef.current = renderPointsRef.current.filter(
      (p) => p.id === pinId || p.id === haloId,
    );
    // If artists layer filtered them out, synthesize a minimal pin from the point.
    if (shotPointsOverrideRef.current.length === 0) {
      const style = POINT_KIND_STYLES[point.kind];
      shotPointsOverrideRef.current = [
        {
          ...point,
          id: haloId,
          color: PIN_HALO_COLOR,
          radius: pointRadiusForCount(point.kind, point.artistCount, 1) * 1.7,
          altitude: style.altitude * 0.4,
          isHalo: true,
        },
        {
          ...point,
          color: style.color,
          radius: pointRadiusForCount(point.kind, point.artistCount, 1),
          altitude: style.altitude,
        },
      ];
    }
    pushPoints(globe);
    showShotCountryLabel(globe, point);
  }

  function exitShotPerf(globe: GlobeInstance) {
    shotPerfRef.current = false;
    shotLabelPointRef.current = null;
    shotPointsOverrideRef.current = null;
    applyClouds(layersRef.current.clouds);
    applyBorders(globe, layersRef.current.borders);
    pushPoints(globe);
    globe.arcsData(arcsRef.current);
    globe.objectsData(objectsRef.current);
    globe.labelAltitude(LABEL_ALTITUDE);
    refreshLabels(globe);
  }

  function refreshLabels(globe: GlobeInstance) {
    if (shotPerfRef.current && shotLabelPointRef.current) {
      showShotCountryLabel(globe, shotLabelPointRef.current);
      return;
    }
    if (!layersRef.current.borders) {
      globe.labelsData([]);
      return;
    }
    const alt = globe.pointOfView().altitude;
    if (alt > LABEL_MAX_ALTITUDE) {
      globe.labelsData([]);
      return;
    }
    // Artist countries only — empty-country labels are the soup + cost.
    const labels: CountryLabel[] = [];
    for (const point of pointsByCodeRef.current.values()) {
      if (!point.countryCode) continue;
      labels.push({
        lat: point.lat,
        lng: point.lng,
        text: point.label,
        color: LABEL_COLOR_ARTIST,
        size: LABEL_SIZE_ARTIST,
        hasArtists: true,
      });
    }
    globe.labelsData(labels);
  }

  /**
   * Day/night ON → terminator shader + sun clock.
   * OFF → plain day phong (must replace custom ShaderMaterial — globeImageUrl
   * alone only sets .map and leaves the shader stuck on).
   */
  function applyDayNight(globe: GlobeInstance, on: boolean) {
    if (on) {
      const day = dayTexRef.current;
      const night = nightTexRef.current;
      if (day && night) {
        globe.globeMaterial(ensureSunMaterial(day, night));
      }
      return;
    }
    let phong = dayPhongRef.current;
    if (!phong) {
      phong = new MeshPhongMaterial({ color: 0xffffff });
      dayPhongRef.current = phong;
    }
    const day = dayTexRef.current;
    if (day) {
      phong.map = day;
      phong.needsUpdate = true;
    }
    globe.globeMaterial(phong);
    globe.globeImageUrl(dayTextureUrlRef.current);
  }

  function applyStars(globe: GlobeInstance, on: boolean) {
    globe.backgroundImageUrl(on ? GLOBE_ASSETS.nightSky : null);
  }

  function ensureSunMaterial(
    dayTex: Texture,
    nightTex: Texture,
  ): ShaderMaterial {
    const existing = sunMaterialRef.current;
    if (existing) {
      existing.uniforms["dayTexture"]!.value = dayTex;
      existing.uniforms["nightTexture"]!.value = nightTex;
      return existing;
    }
    const material = new ShaderMaterial({
      uniforms: {
        dayTexture: { value: dayTex },
        nightTexture: { value: nightTex },
        sunPosition: { value: new Vector2() },
        globeRotation: { value: new Vector2() },
      },
      vertexShader: DAY_NIGHT_SHADER.vertexShader,
      fragmentShader: DAY_NIGHT_SHADER.fragmentShader,
    });
    sunMaterialRef.current = material;
    const sun = clockRef.current.sun;
    setVec2Uniform(material.uniforms, "sunPosition", sun.lng, sun.lat);
    return material;
  }

  function applyClouds(on: boolean) {
    const clouds = cloudsRef.current;
    if (!clouds) return;
    clouds.visible = on;
  }

  async function runFocus(point: GlobePoint) {
    const globe = globeRef.current;
    if (!globe || flyingRef.current) return;
    flyingRef.current = true;
    focusLockRef.current = true;
    try {
      await browseFlyTo(globe, { lat: point.lat, lng: point.lng });
      onFocusCompleteRef.current(point.id);
      refreshLabels(globe);
    } finally {
      flyingRef.current = false;
    }
  }

  function applyRecordViewport(aspect: ShotAspect | null) {
    const host = hostRef.current;
    const el = containerRef.current;
    const globe = globeRef.current;
    if (!host || !el || !globe) return;

    recordViewportRef.current = aspect;
    setRecordAspect(aspect);
    if (!aspect) {
      el.style.width = "";
      el.style.height = "";
      el.style.transform = "";
      globe.width(host.clientWidth);
      globe.height(host.clientHeight);
      return;
    }

    const { width, height } = aspectPixelSize(aspect);
    const hostW = Math.max(1, host.clientWidth);
    const hostH = Math.max(1, host.clientHeight);
    const scale = Math.min(hostW / width, hostH / height);
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    el.style.transform = `scale(${scale})`;
    globe.width(width);
    globe.height(height);
  }

  async function runPreview(
    point: GlobePoint,
    preset: ShotPresetValues,
    aspect: ShotAspect,
  ) {
    const globe = globeRef.current;
    if (!globe || flyingRef.current) return;
    flyingRef.current = true;
    focusLockRef.current = true;
    applyRecordViewport(aspect);
    enterShotPerf(globe, point);
    if (preset.highlightBorder && point.countryCode) {
      setHighlightCountry(globe, point.countryCode);
    }
    try {
      // Let the letterboxed resize settle before the flight so framing matches record.
      await new Promise<void>((r) => {
        requestAnimationFrame(() => r());
      });
      await runPreviewShot(
        globe,
        { lat: point.lat, lng: point.lng },
        preset,
        aspect,
      );
      onFocusCompleteRef.current(point.id);
    } finally {
      setHighlightCountry(globe, null);
      exitShotPerf(globe);
      applyRecordViewport(null);
      flyingRef.current = false;
    }
  }

  async function runRecord(
    point: GlobePoint,
    preset: ShotPresetValues,
    aspect: ShotAspect,
    hooks: {
      enterCleanStage: () => void;
      exitCleanStage: () => void;
    },
    signal?: AbortSignal,
  ): Promise<Blob> {
    const globe = globeRef.current;
    if (!globe) throw new Error("Globe not ready");
    if (flyingRef.current) throw new Error("Globe is busy");
    flyingRef.current = true;
    focusLockRef.current = true;
    enterShotPerf(globe, point);
    if (preset.highlightBorder && point.countryCode) {
      setHighlightCountry(globe, point.countryCode);
    }
    try {
      const blob = await recordFlight(
        globe,
        { lat: point.lat, lng: point.lng },
        preset,
        aspect,
        {
          enterRecordViewport: (a) => {
            applyRecordViewport(a);
          },
          exitRecordViewport: () => {
            applyRecordViewport(null);
          },
          enterCleanStage: hooks.enterCleanStage,
          exitCleanStage: hooks.exitCleanStage,
          getCanvas: () => {
            const el = containerRef.current;
            return el?.querySelector("canvas") ?? null;
          },
        },
        signal,
      );
      onFocusCompleteRef.current(point.id);
      return blob;
    } finally {
      setHighlightCountry(globe, null);
      exitShotPerf(globe);
      flyingRef.current = false;
    }
  }

  useImperativeHandle(ref, () => ({
    focusOnPoint: runFocus,
    previewShot: runPreview,
    recordShot: runRecord,
    getGlobe: () => globeRef.current,
    isBusy: () => flyingRef.current,
  }));

  // Mount once.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let disposed = false;
    let raf = 0;
    let lastTs = performance.now();

    const globe = new Globe(el, { animateIn: true })
      .backgroundColor("#000000")
      .showAtmosphere(true)
      .atmosphereColor(ATMOSPHERE_COLOR)
      .atmosphereAltitude(ATMOSPHERE_ALTITUDE)
      .pointsMerge(false)
      .pointLat("lat")
      .pointLng("lng")
      .pointAltitude("altitude")
      .pointRadius("radius")
      .pointColor("color")
      .pointLabel((d: object) => {
        if (tooltipsOffRef.current) return "";
        const p = d as RenderPoint;
        if (p.isHalo) return "";
        return `${p.label} · ${p.artistCount} artist${p.artistCount === 1 ? "" : "s"}`;
      })
      .arcsData([])
      .arcStartLat("startLat")
      .arcStartLng("startLng")
      .arcEndLat("endLat")
      .arcEndLng("endLng")
      .arcColor("color")
      .arcAltitude("altitude")
      .arcStroke(0.6)
      .arcDashLength(0.4)
      .arcDashGap(0.6)
      .arcDashAnimateTime(2200)
      .objectsData([])
      .objectLat("lat")
      .objectLng("lng")
      .objectAltitude("altitude")
      .objectFacesSurface(false)
      .objectThreeObject((d: object) => glowSphere((d as PlaylistObject).color))
      .objectLabel((d: object) =>
        tooltipsOffRef.current ? "" : (d as PlaylistObject).name,
      )
      .pathsData([])
      .pathPoints("coords")
      .pathPointLng((p: number[]) => p[0]!)
      .pathPointLat((p: number[]) => p[1]!)
      .pathPointAlt(() => BORDER_ALTITUDE)
      .pathColor(() => BORDER_STROKE)
      .pathStroke(BORDER_STROKE_WIDTH)
      .pathDashLength(1)
      .pathDashGap(0)
      .pathDashAnimateTime(0)
      .pathTransitionDuration(0)
      .labelsData([])
      .labelLat("lat")
      .labelLng("lng")
      .labelText("text")
      .labelColor("color")
      .labelSize("size")
      .labelAltitude(LABEL_ALTITUDE)
      .labelDotRadius(0)
      .labelResolution(2)
      .ringsData([])
      .ringColor(() => (t: number) => `rgba(30,215,96,${Math.sqrt(1 - t)})`)
      .ringMaxRadius("maxR")
      .ringPropagationSpeed("propagationSpeed")
      .ringRepeatPeriod("repeatPeriod");

    // Cap zoom so 8K never reveals mush (country-framing floor).
    const radius = globe.getGlobeRadius();
    globe.controls().minDistance = radius * (1 + MIN_CAMERA_ALTITUDE);
    globe.controls().autoRotate = true;
    globe.controls().autoRotateSpeed = 0.35;
    globe.controls().enableDamping = true;

    const bumpIdle = () => {
      if (flyingRef.current) return;
      focusLockRef.current = false;
      onUserInteractRef.current();
      globe.controls().autoRotate = false;
      if (idleTimer.current) clearTimeout(idleTimer.current);
      idleTimer.current = setTimeout(() => {
        if (!focusLockRef.current) globe.controls().autoRotate = true;
      }, IDLE_RESUME_MS);
    };
    el.addEventListener("pointerdown", bumpIdle);
    el.addEventListener("wheel", bumpIdle, { passive: true });

    globe.onZoom(({ lng, lat, altitude }) => {
      cameraAltRef.current = altitude;
      pushPoints(globe);
      if (layersRef.current.dayNight) {
        const mat = sunMaterialRef.current;
        if (mat) setVec2Uniform(mat.uniforms, "globeRotation", lng, lat);
      }
      // Shot label stays at every altitude; browse labels still hide when far.
      if (shotPerfRef.current) {
        if (shotLabelPointRef.current) {
          showShotCountryLabel(globe, shotLabelPointRef.current);
        }
        return;
      }
      if (altitude > LABEL_MAX_ALTITUDE) globe.labelsData([]);
      else if (layersRef.current.borders) refreshLabels(globe);
    });

    // Borders are static paths — no polygon layer, no country-mesh click.
    // Artist pins own the click → sheet / cinematic focus.

    globe.onPointClick((d: object) => {
      const p = d as RenderPoint;
      if (p.isHalo || p.kind !== "country" || !p.countryCode) return;
      if (cleanStageRef.current) {
        void runFocus(p);
        return;
      }
      let playlistId: string | null = null;
      for (const id of selectedRef.current) {
        const pl = playlistsByIdRef.current.get(id);
        if (pl?.artistCountryCodes.includes(p.countryCode)) {
          playlistId = id;
          break;
        }
      }
      onCountryClickRef.current(p, playlistId);
    });

    globe.onObjectClick((d: object) => {
      onPlaylistObjectClickRef.current((d as PlaylistObject).id);
    });

    globeRef.current = globe;

    const host = hostRef.current;
    const ro = new ResizeObserver(() => {
      if (recordViewportRef.current) {
        applyRecordViewport(recordViewportRef.current);
        return;
      }
      const h = hostRef.current;
      if (!h) return;
      globe.width(h.clientWidth);
      globe.height(h.clientHeight);
    });
    if (host) ro.observe(host);
    if (host) {
      globe.width(host.clientWidth);
      globe.height(host.clientHeight);
    }

    // First paint: low-res day + countries + clouds. Hi-res textures lazy-swap.
    // Day-only mode starts on the day image (no shader) until dayNight is on.
    globe.globeImageUrl(GLOBE_ASSETS.earthDay);
    dayTextureUrlRef.current = GLOBE_ASSETS.earthDay;

    const loader = new TextureLoader();
    const reduceMotion = prefersReducedMotion();

    void Promise.all([
      loader.loadAsync(GLOBE_ASSETS.earthDay),
      loader.loadAsync(GLOBE_ASSETS.earthNight),
      loader.loadAsync(GLOBE_ASSETS.clouds),
      fetch(GLOBE_ASSETS.countries)
        .then((r) => r.json())
        .then((body) => CountriesGeoJsonSchema.parse(body)),
    ]).then(([dayTex, nightTex, cloudsTex, geo]) => {
      if (disposed) return;

      dayTexRef.current = dayTex;
      nightTexRef.current = nightTex;

      if (layersRef.current.dayNight) {
        ensureSunMaterial(dayTex, nightTex);
      }

      borderPathsRef.current = geojsonToBorderPaths(
        geo.features.filter(
          (f) => Boolean(f.properties.ISO_A2) && f.properties.ISO_A2 !== "AQ",
        ),
      );

      const clouds = new Mesh(
        new SphereGeometry(globe.getGlobeRadius() * (1 + CLOUDS_ALT), 75, 75),
        new MeshPhongMaterial({
          map: cloudsTex,
          transparent: true,
          opacity: CLOUDS_OPACITY,
          // depthWrite must stay true — false lets the globe (city lights in the
          // night texture) paint over the cloud sphere and look "under" terrain.
          depthWrite: true,
        }),
      );
      // Above the globe mesh, below pins/arcs (those are separate globe.gl layers).
      clouds.renderOrder = 1;
      globe.scene().add(clouds);
      cloudsRef.current = clouds;

      applyDayNight(globe, layersRef.current.dayNight);
      applyStars(globe, layersRef.current.stars);
      applyClouds(layersRef.current.clouds);
      applyBorders(globe, layersRef.current.borders);

      readyRef.current = true;
      onReadyRef.current();

      // Lazy-load hi-res; swap when ready (first paint already showing low-res).
      void Promise.all([
        loader.loadAsync(GLOBE_ASSETS.earthDayHi),
        loader.loadAsync(GLOBE_ASSETS.earthNightHi),
      ]).then(([dayHi, nightHi]) => {
        if (disposed) return;
        dayTexRef.current = dayHi;
        nightTexRef.current = nightHi;
        dayTextureUrlRef.current = GLOBE_ASSETS.earthDayHi;
        const mat = sunMaterialRef.current;
        if (mat) {
          mat.uniforms["dayTexture"]!.value = dayHi;
          mat.uniforms["nightTexture"]!.value = nightHi;
        }
        // Day-only mode: refresh the phong map to hi-res.
        if (!layersRef.current.dayNight) {
          globe.globeImageUrl(GLOBE_ASSETS.earthDayHi);
        }
      });

      const tick = (ts: number) => {
        if (disposed) return;
        const delta = ts - lastTs;
        lastTs = ts;
        // Freeze ambiance extras during shot capture — saves GPU for the flight.
        if (!shotPerfRef.current) {
          if (layersRef.current.dayNight && sunMaterialRef.current) {
            clockRef.current = advanceAmbianceClock(clockRef.current, delta);
            const { lng, lat } = clockRef.current.sun;
            setVec2Uniform(
              sunMaterialRef.current.uniforms,
              "sunPosition",
              lng,
              lat,
            );
          }
          const cloudsMesh = cloudsRef.current;
          if (cloudsMesh && layersRef.current.clouds && !reduceMotion) {
            cloudsMesh.rotation.y += (CLOUDS_ROTATION_SPEED * Math.PI) / 180;
          }
        }
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    });

    return () => {
      disposed = true;
      cancelAnimationFrame(raf);
      el.removeEventListener("pointerdown", bumpIdle);
      el.removeEventListener("wheel", bumpIdle);
      if (idleTimer.current) clearTimeout(idleTimer.current);
      ro.disconnect();
      const clouds = cloudsRef.current;
      if (clouds) {
        globe.scene().remove(clouds);
        clouds.geometry.dispose();
        const m = clouds.material;
        if (!Array.isArray(m)) m.dispose();
      }
      sunMaterialRef.current?.dispose();
      dayPhongRef.current?.dispose();
      globe._destructor();
      globeRef.current = null;
      cloudsRef.current = null;
      sunMaterialRef.current = null;
      dayPhongRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount once
  }, []);

  // Layer visibility applicator — registry-driven, not scattered if/else in the page.
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe || !readyRef.current || shotPerfRef.current) return;
    applyDayNight(globe, layers.dayNight);
    applyStars(globe, layers.stars);
    applyClouds(layers.clouds);
    applyBorders(globe, layers.borders);
    // apply* close over refs; intentional mount-stable applicator.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers.dayNight, layers.stars, layers.clouds, layers.borders]);

  // Auto-rotate lock after focus.
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    if (focusLockRef.current) {
      globe.controls().autoRotate = false;
    }
  }, [focusedPointId]);

  // Data layers.
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;
    renderPointsRef.current = renderPoints;
    arcsRef.current = arcs;
    objectsRef.current = objects;
    if (shotPerfRef.current) return; // shot owns the stage until exitShotPerf
    pushPoints(globe);
    globe.arcsData(arcs);
    globe.objectsData(objects);
    // pushPoints closes over refs; scale follows cameraAltRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderPoints, arcs, objects]);

  return (
    <div
      ref={hostRef}
      className="absolute inset-0 flex items-center justify-center overflow-hidden bg-bg"
    >
      <div
        ref={containerRef}
        className={cn(
          "origin-center",
          recordAspect ? "shrink-0" : "absolute inset-0 size-full",
        )}
      />
    </div>
  );
});
